<template>
  <div class="image-magnifier-container">
    <div class="product-image-wrapper" :style="{ position: 'relative' }">
      <img 
        :src="imageSrc" 
        class="product-image" 
        :alt="altText" 
        @load="onImageLoad"
        @error="onImageError"
      />
      <button 
        v-if="imageLoaded && !isXRayEnabled"
        class="magnify-btn" 
        @click="openMagnifier"
        :aria-label="'Zoom Image'"
        tabindex="0"
      >
        <img src="/icons/icon-magnify.svg" alt="Magnify" class="magnify-icon" />
      </button>
    </div>

    <div 
      v-if="isOpen" 
      class="magnifier-overlay" 
      :style="overlayStyle"
      @click.stop.prevent="closeMagnifier"
      @keydown.esc="closeMagnifier"
      tabindex="0"
    >
      <div class="magnifier-content" @click.stop>
        <button 
          class="close-magnifier-btn" 
          @click.stop.prevent="closeMagnifier"
          :aria-label="'Close Image'"
        >
          <img src="/icons/icon-close.svg" alt="Close" class="close-icon" />
        </button>
        <img 
          :src="imageSrc" 
          class="magnified-image" 
          :alt="altText"
          @load="onImageLoad"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'ImageMagnifier',
  props: {
    imageSrc: {
      type: String,
      required: true
    },
    altText: {
      type: String,
      default: 'Product image'
    }
  },
  data() {
    return {
      isOpen: false,
      imageLoaded: false,
      headerHeight: 0
    }
  },
  computed: {
    ...mapGetters(['isXRayEnabled']),
    overlayStyle() {
      return {
        paddingTop: `${this.headerHeight}px`
      };
    }
  },
  methods: {
    openMagnifier() {
      this.calculateHeaderHeight();
      this.isOpen = true;
      document.body.style.overflow = 'hidden';
      this.$nextTick(() => {
        const overlay = this.$el.querySelector('.magnifier-overlay');
        if (overlay) overlay.focus();
      });
    },
    closeMagnifier() {
      this.isOpen = false;
      document.body.style.overflow = '';
    },
    onImageLoad() {
      this.imageLoaded = true;
    },
    onImageError() {
      this.imageLoaded = true;
    },
    calculateHeaderHeight() {
  
      let headerHeight = 0;
    
      const header = document.querySelector('.feature-header');
      if (header) {
        const rect = header.getBoundingClientRect();
        if (rect.height > 0) {
          headerHeight = rect.height;
        }
      }
      
      if (headerHeight === 0) {
        headerHeight = 80;
      }
      
      this.headerHeight = headerHeight;
    }
  },
  mounted() {
    this.calculateHeaderHeight();
  },
  watch: {
    imageSrc() {
      this.imageLoaded = false;
    }
  },
  beforeDestroy() {
    document.body.style.overflow = '';
  }
}
</script>

<style lang="scss" scoped>
.image-magnifier-container {
  position: relative;
  display: inline-block;
  width: 100%;
}

.product-image-wrapper {
  position: relative;
  display: inline-block;
  width: 100%;
}

.product-image {
  display: block;
  max-height: 200px;
	max-width: 90%;
	width: auto;
	height: auto;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	margin: auto;
}

.magnify-btn {
  position: absolute;
  bottom: 0px;
  right: 8px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }

  &:focus {
    outline: 2px solid #007bff;
    outline-offset: 2px;
  }

  .magnify-icon {
    width: 20px;
    height: 20px;
    filter: invert(1);
  }
}

.magnifier-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 20px;

  &:focus {
    outline: none;
  }
}

.magnifier-content {
  position: relative;
  max-width: 80vw;
  max-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
}

.close-magnifier-btn {
  position: absolute;
  top: -40px;
  right: 0;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10000;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }

  &:focus {
    outline: 2px solid #007bff;
    outline-offset: 2px;
  }

  .close-icon {
    width: 24px;
    height: 24px;
    filter: invert(1);
  }
}

.magnified-image {
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.8);
  max-width: 70vw;
  max-height: 70vh;
}

@media (max-width: 768px) {
  .magnify-btn {
    width: 32px;
    height: 32px;
    bottom: 6px;
    right: 6px;

    .magnify-icon {
      width: 18px;
      height: 18px;
    }
  }

  .close-magnifier-btn {
    top: -35px;
    width: 35px;
    height: 35px;

    .close-icon {
      width: 20px;
      height: 20px;
    }
  }

  .magnifier-overlay {
    padding: 10px;
  }

  .magnifier-content {
    max-width: 95vw;
    max-height: 95vh;
  }

  .magnified-image {
    max-width: 60vw;
    max-height: 50vh;
  }

  .product-image {
    max-height: 180px;
    max-width: 85px;
  }
}
</style>
